import { GraphQL } from "@rpi/openapi-api";

/**
 * Generate all 12 months for a given year as Date objects
 */
function generateAllMonths(year: number): Date[] {
  return Array<Date>(12)
    .fill(new Date())
    .map((_, index) => {
      return new Date(year, index);
    });
}

type OrderDataPoint = NonNullable<GraphQL.OrdersChartQuery["ordersChart"]>["dataPoints"][number];
export type TransformedOrderDataPoint = {
  month: Date;
  shipped: number;
  printing: number;
  inProgress: number;
  failed: number;
};

function transformOrderDataPoint(dataPoint: OrderDataPoint): TransformedOrderDataPoint {
  const result: TransformedOrderDataPoint = {
    month: new Date(dataPoint.month),
    shipped: 0,
    printing: 0,
    inProgress: 0,
    failed: 0,
  };
  for (const sc of dataPoint.statusCounts) {
    switch (sc.status) {
      case GraphQL.PublicOrderStatus.Shipped:
        result.shipped = sc.count;
        break;
      case GraphQL.PublicOrderStatus.Printing:
        result.printing = sc.count;
        break;
      case GraphQL.PublicOrderStatus.Queued:
      case GraphQL.PublicOrderStatus.Received:
      case GraphQL.PublicOrderStatus.Validating:
      case GraphQL.PublicOrderStatus.ValidAwaitingPayment:
      case GraphQL.PublicOrderStatus.ValidHoldingBin:
        result.inProgress += sc.count;
        break;
      case GraphQL.PublicOrderStatus.Failed:
        result.failed += sc.count;
        break;
    }
  }

  return result;
}

/**
 * Fill in missing months with zero values for order data
 */
export function fillMissingOrderMonths(dataPoints: OrderDataPoint[], year: number): TransformedOrderDataPoint[] {
  const allMonths = generateAllMonths(year);

  return allMonths.map((monthDate) => {
    // Find matching data point by comparing year and month
    const dataPoint = dataPoints.find((dp) => {
      const date = new Date(dp.month);
      return date.getFullYear() === monthDate.getFullYear() && date.getMonth() === monthDate.getMonth();
    });

    if (dataPoint) {
      return transformOrderDataPoint(dataPoint);
    }
    return {
      month: monthDate,
      shipped: 0,
      printing: 0,
      inProgress: 0,
      failed: 0,
    };
  });
}

type PaymentDataPoint = NonNullable<GraphQL.PaymentsChartQuery["paymentsChart"]>["dataPoints"][number];

export type TransformedPaymentDataPoint = {
  month: Date;
  netPaymentAmount: number;
  currency: GraphQL.SupportedCurrency;
};

function transformPaymentDataPoint(dataPoint: PaymentDataPoint): TransformedPaymentDataPoint {
  return {
    month: new Date(dataPoint.month),
    netPaymentAmount: dataPoint.netPaymentAmount,
    currency: dataPoint.currency,
  };
}

/**
 * Fill in missing months with zero values for payment data
 */
export function fillMissingPaymentMonths(
  dataPoints: PaymentDataPoint[],
  year: number,
  defaultCurrency: GraphQL.SupportedCurrency = GraphQL.SupportedCurrency.Usd
): TransformedPaymentDataPoint[] {
  const allMonths = generateAllMonths(year);

  // Get currency from first data point if available, otherwise use default
  const currency = dataPoints.length > 0 ? dataPoints[0].currency : defaultCurrency;

  return allMonths.map((monthDate) => {
    // Find matching data point by comparing year and month
    const dataPoint = dataPoints.find((dp) => {
      const date = new Date(dp.month);
      return date.getFullYear() === monthDate.getFullYear() && date.getMonth() === monthDate.getMonth();
    });

    if (dataPoint) {
      return transformPaymentDataPoint(dataPoint);
    }
    return {
      month: monthDate,
      netPaymentAmount: 0,
      currency,
    };
  });
}
